import { useState } from "react";
import { ChevronDown, ChevronRight, FileText } from "lucide-react";

function getDotColor (severity) {
  switch (severity) {
    case "high":
      return "bg-orange-500";
    case "medium":
      return "bg-yellow-400";
    case "low":
      return "bg-green-400";
    default:
      return "bg-gray-300";
  }
}

export default function TimelineEvent ({ mailDetails, fileDetails }) {
  const [expanded, setExpanded] = useState(false);

  const severity = "high";
  const title = mailDetails?.customer_name || "Untitled";
  const timestamp = new Date(mailDetails?.mail_received_at).toLocaleString();
  const actor = mailDetails?.sender_mail_id || "Unknown Sender";

  return (
    <div className="relative pl-6">
      {/* Icon circle */}
      <div
        className={`absolute -left-[20px] top-4 w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center ${getDotColor(
          severity
        )}`}
      >
        <FileText size={12} className="text-white" />
      </div>

      <div
        className="bg-gray-50 rounded-md border shadow-sm p-4 pr-6 relative"
        // onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-sm font-medium">{title}</span>
            <span className=" bg-blue-100 text-blue-700 border-blue-200 border-2 px-2 py-0.5 rounded-full font-medium">
              Email
            </span>
            {fileDetails?.some(file => file.is_acord) && (
              <span className=" bg-green-100 text-green-700 border-green-200 border-2 px-2 py-0.5 rounded-full font-medium">
                ACORD Form
              </span>
            )}
          </div>
          <span className=" text-gray-500">
            {actor} • {timestamp}
          </span>
          <p className="text-sm text-gray-700 mt-1">
            {mailDetails?.mail_summary}
          </p>
        </div>

        {/* Expanded Section */}
        {expanded && (
          <div className="text-sm bg-gray-50 py-3 space-y-4 mt-3">
            {/* Email Details */}
            <div>
              <h4 className=" font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                Email Details
              </h4>
              <div className="grid grid-cols-2 gap-2  text-gray-700">
                <div className="p-2 bg-white rounded border">
                  <strong>Subject:</strong> {mailDetails?.mail_subject}
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong>Sender:</strong> {mailDetails?.sender_mail_id}
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong>Receiver:</strong> {mailDetails?.receiver_mail_id}
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong>Received At:</strong> {timestamp}
                </div>
                <div className="p-2 bg-white rounded border col-span-2">
                  <strong>Attachments:</strong> {mailDetails?.attachment_count}{" "}
                  files
                </div>
              </div>
            </div>

            {/* Attached Documents */}
            {fileDetails && fileDetails.length > 0 && (
              <div>
                <h4 className=" font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                  Documents ({fileDetails.length})
                </h4>
                <div className="space-y-2">
                  {fileDetails.map((file, index) => (
                    <div key={index} className="p-3 border bg-white rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex">
                          {file.content_based_name}
                          {file.gs_link && (
                            <a
                              href={file.gs_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 ml-2 flex items-center"
                            >
                              <FileText className="h-5 w-5 flex text-orange-500" />
                            </a>
                          )}
                        </span>
                      </div>
                      {file.summary_extraction && (
                        <div
                          className="text-gray-600 "
                          dangerouslySetInnerHTML={{
                            __html: file.summary_extraction
                          }}
                        />
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {file.is_application && (
                          <span className=" bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            Application
                          </span>
                        )}
                        {file.is_acord && (
                          <span className=" bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            ACORD
                          </span>
                        )}
                        {file.total_pages && (
                          <span className=" bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                            {file.total_pages} pages
                          </span>
                        )}
                        {file.size_in_mb && (
                          <span className=" bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full">
                            {file.size_in_mb.toFixed(2)} MB
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email Flow */}
            {mailDetails?.mail_transaction_flow && (
              <div>
                <h4 className="font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  Email Flow
                </h4>
                <div className="space-y-2">
                  {mailDetails.mail_transaction_flow.map((flow, index) => (
                    <div key={index} className="p-3 bg-white rounded border">
                      <div className="grid grid-cols-[350px,200px,350px,auto] items-center gap-4">
                        <div className="overflow-hidden">
                          <span className="text-gray-500 mr-2">From:</span>
                          <span className="font-medium" title={flow.sender}>
                            {flow.sender}
                          </span>
                        </div>

                        <div className="text-center">
                          <ChevronRight className="inline h-5 w-5 text-gray-400" />
                        </div>

                        <div className="overflow-hidden">
                          <span className="text-gray-500 mr-2">To:</span>
                          <span className="font-medium" title={flow.receiver}>
                            {flow.receiver}
                          </span>
                        </div>

                        <div className="text-right text-gray-500 text-sm">
                          {new Date(parseInt(flow.time)).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className="absolute right-2 top-4 flex items-center gap-2 text-gray-500 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className={`${getDotColor(severity)}`} />
          {expanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
        </div>
      </div>
    </div>
  );
}
